// Voice-to-text for the notes fields (04 §2).
//
// Uses the browser's own SpeechRecognition, which on Chrome means Google's service. Two
// consequences the design takes seriously:
//
//   · It is OFFERED, never assumed. A browser without it renders nothing here — no disabled
//     button, no tooltip explaining a missing feature. The textarea works exactly as before.
//   · It is push-to-talk and it stops on its own. A microphone that stays open because a click
//     was missed is not a feature, and the owner is dictating notes about named people.
//
// Recognised text is appended, never substituted: a mis-heard word should be corrected by typing
// over it, not by losing what was already written.

import { useEffect, useRef, useState } from 'react';

interface RecognitionResult {
  isFinal: boolean;
  0: { transcript: string };
}

interface RecognitionEvent {
  resultIndex: number;
  results: { length: number; [index: number]: RecognitionResult };
}

interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  onresult: ((event: RecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}

type RecognitionCtor = new () => Recognition;

function recognitionCtor(): RecognitionCtor | null {
  const scope = window as unknown as Record<string, unknown>;
  const ctor = scope['SpeechRecognition'] ?? scope['webkitSpeechRecognition'];
  return typeof ctor === 'function' ? (ctor as RecognitionCtor) : null;
}

export function Dictation({ onText }: { onText: (text: string) => void }) {
  const [listening, setListening] = useState(false);
  const recognition = useRef<Recognition | null>(null);
  const available = recognitionCtor() !== null;

  useEffect(
    () => () => {
      // Leaving the screen must close the microphone. Nothing keeps listening off-screen.
      recognition.current?.stop();
    },
    [],
  );

  if (!available) return null;

  const toggle = (): void => {
    if (listening) {
      recognition.current?.stop();
      return;
    }

    const Ctor = recognitionCtor();
    if (Ctor === null) return;

    const engine = new Ctor();
    engine.lang = 'bg-BG';
    engine.continuous = false;
    engine.interimResults = false;

    engine.onresult = (event) => {
      for (let index = event.resultIndex; index < event.results.length; index++) {
        const result = event.results[index];
        if (result?.isFinal) onText(result[0].transcript.trim());
      }
    };
    engine.onend = () => setListening(false);
    engine.onerror = () => setListening(false);

    recognition.current = engine;
    engine.start();
    setListening(true);
  };

  return (
    <button
      type="button"
      className={`dictate${listening ? ' dictate--on' : ''}`}
      onClick={toggle}
      title={listening ? 'Спри диктовката' : 'Диктовка'}
      aria-pressed={listening}
    >
      {listening ? '● Слуша' : '🎤'}
    </button>
  );
}
